import { computed, inject, Injectable, InjectionToken, Signal } from '@angular/core';
import { NavDescriptor, NavSurface } from './nav-descriptor';

/**
 * Multi-provider token. Every navigation node is contributed by
 * providing a {@link NavDescriptor} against this token with
 * `multi: true`. The {@link NavRegistry} collects them all and
 * assembles the navigation tree.
 */
export const NAV_DESCRIPTOR = new InjectionToken<NavDescriptor>('NAV_DESCRIPTOR');

/**
 * A flat, searchable index entry derived from the navigation tree.
 * Used to power command-palette / search-style navigation.
 */
export interface NavPageIndexEntry {
  id: string;
  label: string;
  path: string;
  group?: string;
}

/**
 * Central registry of all contributed {@link NavDescriptor}s.
 *
 * Phase 1: pure runtime-state plumbing. Collects every descriptor
 * provided via {@link NAV_DESCRIPTOR}, assembles a stable ordered
 * tree (by `group` then `order` then `label`), and exposes lookup,
 * per-surface selection and a flat searchable page index. NOTHING
 * consumes this yet — it is the seam future surfaces bind to.
 */
@Injectable({ providedIn: 'root' })
export class NavRegistry {
  private readonly _descriptors: readonly NavDescriptor[] = inject(NAV_DESCRIPTOR, {
    optional: true,
  }) as unknown as readonly NavDescriptor[] ?? [];

  /**
   * The assembled navigation tree: top-level descriptors ordered by
   * `group`, then `order`, then `label`. Children are ordered with
   * the same comparator, recursively.
   */
  public readonly tree: Signal<NavDescriptor[]> = computed(() =>
    this._sort(this._descriptors as NavDescriptor[]),
  );

  /** Fast lookup of a descriptor by id (searches the whole tree). */
  public descriptor(id: string): NavDescriptor | undefined {
    return this._flatten(this._descriptors as NavDescriptor[]).find((d) => d.id === id);
  }

  /**
   * Top-level descriptors that opt into the given surface, ordered.
   * A descriptor with no `surfaces` is considered surface-agnostic
   * and is included on every surface.
   */
  public itemsForSurface(surface: NavSurface): NavDescriptor[] {
    return this._sort(
      (this._descriptors as NavDescriptor[]).filter(
        (d) => !d.surfaces || d.surfaces.includes(surface),
      ),
    );
  }

  /**
   * Direct children of the descriptor identified by `parentNavId` that
   * opt into the given `surface`, ordered with the same comparator as
   * the tree. A child with no `surfaces` is surface-agnostic and is
   * included on every surface. Returns `[]` when the parent is unknown
   * or has no matching children.
   *
   * This is the seam routed tab strips bind to: a parent descriptor
   * (e.g. `installations`) declares its tabs as `children` carrying
   * `surfaces: ['tabs', ...]`, and the tab surface reads them here.
   */
  public childrenForSurface(parentNavId: string, surface: NavSurface): NavDescriptor[] {
    const parent = this.descriptor(parentNavId);
    const children = parent?.children ?? [];
    return this._sort(
      children.filter((c) => !c.surfaces || c.surfaces.includes(surface)),
    );
  }

  /**
   * A flat, searchable page index of every node carrying a `path`.
   * `query` matches case-insensitively against label and path.
   *
   * Each entry exposes its owning descriptor's `id`, so callers that
   * need security (e.g. the command palette) can re-resolve
   * `descriptor(id).security` rather than the index carrying security
   * inline. An optional `surface` narrows the index to nodes that opt
   * into that surface (surface-agnostic nodes — no `surfaces` — always
   * pass). Omitting `surface` preserves the original all-nodes
   * behaviour for backward compatibility.
   */
  public pageIndex(query?: string, surface?: NavSurface): NavPageIndexEntry[] {
    let nodes = this._flatten(this._descriptors as NavDescriptor[]);
    if (surface) {
      nodes = nodes.filter((d) => !d.surfaces || d.surfaces.includes(surface));
    }
    const entries = nodes.map((d) => ({
      id: d.id,
      label: d.label,
      path: d.path,
      group: d.group,
    }));
    if (!query) {
      return entries;
    }
    const needle = query.toLowerCase();
    return entries.filter(
      (e) =>
        e.label.toLowerCase().includes(needle) || e.path.toLowerCase().includes(needle),
    );
  }

  private _sort(descriptors: NavDescriptor[]): NavDescriptor[] {
    return [...descriptors]
      .sort(this._compare)
      .map((d) =>
        d.children?.length ? { ...d, children: this._sort(d.children) } : d,
      );
  }

  private _compare = (a: NavDescriptor, b: NavDescriptor): number => {
    const groupA = a.group ?? '';
    const groupB = b.group ?? '';
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.label.localeCompare(b.label);
  };

  private _flatten(descriptors: NavDescriptor[]): NavDescriptor[] {
    const out: NavDescriptor[] = [];
    const walk = (nodes: NavDescriptor[]): void => {
      for (const node of nodes) {
        out.push(node);
        if (node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(descriptors);
    return out;
  }
}
